import { Calendar, Loader2, Move, Bell, GripVertical } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { LANE_COLORS } from './constants';
import { formatDate, formatTime } from './utils';

/**
 * Availability grid — the main slot picker. Renders a time × lane table
 * where each cell can be: the user's own booking (draggable), someone
 * else's booking ("Booked"), a block ("Reserved"), an open slot (Book
 * button), or a multi-slot continuation (skipped cell).
 *
 * Pure presentation: parent owns `availability`, `myBookings`, the drag
 * state, and all helper resolvers (`canReschedule`, `getUserBookingForSlot`,
 * `getCustomerSlotSpanInfo`). Falls back to an "Academy Closed" empty state
 * when `availability.is_open` is false.
 */
const AvailabilityGrid = ({
  availability,
  selectedDate,
  loading,
  isDragging,
  dropTargetSlot,
  canReschedule,
  getUserBookingForSlot,
  getCustomerSlotSpanInfo,
  onOpenBookingModal,
  onOpenWaitlistModal,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  if (!availability?.is_open) {
    return (
      <Card className="border border-slate-200">
        <CardContent className="p-8 lg:p-12 text-center">
          <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
            Academy Closed
          </h3>
          <p className="text-slate-500">
            The academy is closed on this day. Please select another date.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-slate-200 relative">
      {/* Refresh overlay while fetching a new date's availability.
          Keeps the previous grid visible (so layout doesn't jump)
          but dims it and shows a spinner so the user gets instant
          feedback that the grid is updating. */}
      {loading && availability && (
        <div
          className="absolute inset-0 z-20 bg-white/60 backdrop-blur-sm flex items-center justify-center rounded-lg transition-opacity"
          data-testid="availability-refreshing-overlay"
        >
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-slate-200">
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            <span className="text-sm font-medium text-slate-700">
              Loading {formatDate(selectedDate)}…
            </span>
          </div>
        </div>
      )}
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            Available Time Slots
          </CardTitle>
          {isDragging && (
            <Badge variant="secondary" className="bg-blue-100 text-blue-700 animate-pulse">
              <Move className="h-3 w-3 mr-1" />
              Drop on an available slot to reschedule
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1">
          💡 Tip: Drag your booking to an available slot to reschedule
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="p-3 text-left text-sm font-medium text-slate-600 sticky left-0 bg-slate-50">
                  Time
                </th>
                {availability.lanes?.map((lane) => (
                  <th
                    key={lane.lane_id}
                    className={`p-3 text-center text-sm font-medium ${LANE_COLORS[lane.lane_id]?.split(' ')[0]} ${LANE_COLORS[lane.lane_id]?.split(' ')[2]}`}
                  >
                    {lane.lane_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {availability.lanes?.[0]?.slots?.map((slot, idx) => (
                <tr key={slot.start_time} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="p-3 text-sm font-medium text-slate-700 sticky left-0 bg-inherit">
                    {formatTime(slot.start_time)}
                  </td>
                  {availability.lanes?.map((lane) => {
                    const laneSlot = lane.slots[idx];
                    const userBooking = getUserBookingForSlot(lane.lane_id, laneSlot?.start_time);
                    const isDropTarget =
                      dropTargetSlot?.laneId === lane.lane_id &&
                      dropTargetSlot?.startTime === laneSlot?.start_time;

                    const spanInfo = getCustomerSlotSpanInfo(laneSlot, lane, idx);

                    // Continuation cell of a multi-slot booking/block:
                    // render an empty, transparent <td> so the column grid
                    // stays aligned (the starting cell uses absolute
                    // positioning to stretch over this space). Returning
                    // null here would collapse later columns into empty
                    // whitespace.
                    if (spanInfo.skip) {
                      return (
                        <td
                          key={`${lane.lane_id}-${slot.start_time}`}
                          className="p-0"
                        />
                      );
                    }

                    const slotSpan = spanInfo.rowSpan > 1 ? spanInfo.rowSpan : 1;

                    return (
                      <td
                        key={`${lane.lane_id}-${slot.start_time}`}
                        className={`p-2 text-center transition-all duration-200 ${
                          isDropTarget ? 'bg-blue-100 ring-2 ring-blue-400 ring-inset' : ''
                        } ${slotSpan > 1 ? 'relative overflow-visible' : ''}`}
                        onDragOver={(e) =>
                          onDragOver(e, lane.lane_id, laneSlot?.start_time, laneSlot?.is_available)
                        }
                        onDragLeave={onDragLeave}
                        onDrop={(e) =>
                          laneSlot?.is_available && onDrop(e, lane.lane_id, laneSlot?.start_time)
                        }
                      >
                        {userBooking ? (
                          /* User's existing booking - draggable */
                          <div
                            draggable={canReschedule(userBooking) ? 'true' : 'false'}
                            onDragStart={(e) => onDragStart(e, userBooking)}
                            onDragEnd={onDragEnd}
                            onMouseDown={(e) => e.stopPropagation()}
                            className={`
                              w-full px-2 py-1 rounded border-2 text-xs font-medium select-none
                              ${LANE_COLORS[lane.lane_id]}
                              ${canReschedule(userBooking) ? 'cursor-grab active:cursor-grabbing hover:shadow-md' : 'cursor-not-allowed opacity-60'}
                              transition-all duration-200 flex flex-col items-center justify-center
                              ${slotSpan > 1 ? 'absolute left-1 right-1 top-0 z-10' : ''}
                            `}
                            style={
                              slotSpan > 1
                                ? { height: `calc(${slotSpan * 100}% + ${slotSpan - 1}px)` }
                                : undefined
                            }
                            title={
                              canReschedule(userBooking)
                                ? 'Drag to reschedule'
                                : 'Cannot reschedule (less than 2 hours away)'
                            }
                            data-testid={`draggable-booking-${userBooking.id}`}
                          >
                            <div className="flex items-center justify-center gap-1 pointer-events-none">
                              {canReschedule(userBooking) && (
                                <GripVertical className="h-3 w-3 opacity-50" />
                              )}
                              <span>Your Booking</span>
                            </div>
                            {slotSpan > 1 && (
                              <div className="text-[10px] opacity-75 pointer-events-none">
                                {userBooking.start_time} - {userBooking.end_time}
                              </div>
                            )}
                            <div className="text-[10px] opacity-75 pointer-events-none">
                              {userBooking.duration} min
                            </div>
                          </div>
                        ) : laneSlot?.booking_id && spanInfo.type === 'other' ? (
                          /* Other person's booking — opaque block, no details leaked */
                          <div
                            className={`w-full bg-slate-200 border-2 border-slate-300 rounded text-xs text-slate-600 flex items-center justify-center py-2 ${slotSpan > 1 ? 'absolute left-1 right-1 top-0 z-10' : ''}`}
                            style={
                              slotSpan > 1
                                ? { height: `calc(${slotSpan * 100}% + ${slotSpan - 1}px)` }
                                : undefined
                            }
                            data-testid={`booked-${lane.lane_id}-${laneSlot.start_time}`}
                          >
                            <span className="font-medium">Booked</span>
                          </div>
                        ) : !laneSlot?.is_available && !laneSlot?.booking_id && spanInfo.type === 'block' ? (
                          /* Blocked slot — label already redacted server-side for non-admins */
                          <div
                            className={`w-full bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex flex-col items-center justify-center py-2 ${slotSpan > 1 ? 'absolute left-1 right-1 top-0 z-10' : ''}`}
                            style={
                              slotSpan > 1
                                ? { height: `calc(${slotSpan * 100}% + ${slotSpan - 1}px)` }
                                : undefined
                            }
                            data-testid={`blocked-${lane.lane_id}-${laneSlot.start_time}`}
                          >
                            <span className="font-medium">{laneSlot.block_reason || 'Reserved'}</span>
                          </div>
                        ) : laneSlot?.is_available ? (
                          /* Available slot - can book or drop */
                          <Button
                            size="sm"
                            variant="outline"
                            className={`w-full h-8 text-xs transition-all duration-200 ${
                              isDragging
                                ? 'bg-blue-50 border-blue-300 border-dashed text-blue-600 hover:bg-blue-100'
                                : 'bg-green-50 hover:bg-green-100 border-green-300 text-green-700'
                            }`}
                            onClick={() => !isDragging && onOpenBookingModal(lane, laneSlot)}
                            data-testid={`book-${lane.lane_id}-${laneSlot.start_time}`}
                          >
                            {isDragging ? 'Drop Here' : 'Book'}
                          </Button>
                        ) : (
                          /* Other blocked/unavailable slot */
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-slate-400 px-2 py-1 bg-slate-100 rounded">
                              {laneSlot?.block_reason || 'N/A'}
                            </span>
                            {laneSlot?.block_reason === 'Booked' && !userBooking && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-1"
                                onClick={() => onOpenWaitlistModal(lane.lane_id, laneSlot.start_time)}
                                data-testid={`waitlist-${lane.lane_id}-${laneSlot.start_time}`}
                              >
                                <Bell className="h-3 w-3 mr-1" />
                                Waitlist
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AvailabilityGrid;
